const object = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const text = v => typeof v === 'string' && v.trim().length > 0 && v.length <= 2000;
const id = v => typeof v === 'string' && /^[1-9]\d{0,24}$/.test(v);
const token = v => typeof v === 'string' && /^[a-zA-Z][\w-]{0,63}$/.test(v);
const natural = v => Number.isSafeInteger(v) && v >= 0;
const positive = v => natural(v) && v > 0;
const copy = v => JSON.parse(JSON.stringify(v));
const freeze = v => { if (v && typeof v === 'object') { Object.values(v).forEach(freeze); Object.freeze(v); } return v; };
const unique = list => new Set(list).size === list.length;
const contextKeys = ['productId', 'sectionId', 'formId', 'marketId', 'country', 'language', 'currency', 'generation', 'amountDomain'];
export const packCartConnectionLabels = Object.freeze(['add','ready','incomplete','unavailable','missingImages','pending','complete','partial','uncertain','presentationFailed','partialPresentationFailed','failed','reconciling']);
export const packLabels = Object.freeze(['heading', 'eyebrow', 'intro', 'note', 'quantity', 'tier3', 'tier5', 'tier7', 'item', 'colour', 'chooseProduct', 'chooseOption', 'subtotal', 'add', 'selectionOnly', 'incomplete', 'unavailable', 'missingCombination', 'quantityExceeded', 'quantityUnknown', 'invalid', 'ready']);
// The policy describes a required form check, never discount entitlement.
export function packTiers(source) {
  const merchant = source?.propertiesPolicy === 'standard-form-required' && source?.provenance?.id === 'merchant-pack-v1';
  if (!merchant && source?.propertiesPolicy !== 'none') return null;
  const tiers = source.tiers === undefined && !merchant ? [3, 5, 7] : source.tiers;
  return Array.isArray(tiers) && tiers.length >= 1 && tiers.length <= 3 && unique(tiers)
    && tiers.every(n => Number.isSafeInteger(n) && n >= 2 && n <= 10) ? [...tiers] : null;
}
export function validPackProvenance(source) {
  const required = source?.propertiesPolicy === 'standard-form-required'
    ? ['id', 'revision', 'evidence', 'collectionEvidence']
    : ['id', 'revision', 'evidence', 'ruleId', 'ruleRevision', 'ruleEvidence', 'collectionEvidence'];
  return Boolean(packTiers(source) && object(source.provenance) && required.every(key => text(source.provenance[key])));
}
const sameContext = (a, b) => object(a) && object(b) && contextKeys.every(k => a[k] === b[k]);
const safeURL = (url, image = false) => text(url) && (image ? /^https:\/\//.test(url) : /^\/(?!\/)/.test(url)) && !/[\s\\<>]/.test(url);
const unsupported = v => ['properties', 'selling_plan', 'sellingPlan', 'sellingPlanId'].some(k => k in v);

export function validatePack(input) {
  try {
    const c = input?.context, s = input?.source;
    if (!object(input) || input.version !== 1 || input.status !== 'available' || !token(input.instanceId) || !object(c) || !object(s)) return null;
    if (!contextKeys.every(k => text(c[k])) || !id(c.productId) || !id(c.marketId) || !/^[A-Z]{2}$/.test(c.country) || !['GBP', 'USD', 'JPY', 'KRW'].includes(c.currency) || c.amountDomain !== 'shopify-liquid-money-100') return null;
    if (s.status !== 'adopted' || s.kind !== 'native-pack-pool' || !sameContext(s.context, c) || !validPackProvenance(s) || !id(s.collectionId) || s.collectionId !== input.collectionId) return null;
    if (s.complete !== true || !Array.isArray(input.products) || input.products.length < 1 || input.products.length > 20 || s.productCount !== input.products.length || !unique(input.products.map(p => p.id))) return null;
    if (!object(input.labels) || !packLabels.filter(k => !/^tier\d+$/.test(k)).concat(packTiers(s).map(n => `tier${n}`)).every(k => text(input.labels[k]))) return null;
    const variantIds = new Set();
    for (const p of input.products) {
      if (!object(p) || unsupported(p) || !id(p.id) || !text(p.title) || !(p.colour === '' || text(p.colour)) || !safeURL(p.url) || typeof p.available !== 'boolean' || p.requiresSellingPlan !== false || p.sellingPlanCount !== 0 || p.variantsComplete !== true) return null;
      if (p.image !== null && (!object(p.image) || !safeURL(p.image.url, true) || typeof p.image.alt !== 'string' || !positive(p.image.width) || !positive(p.image.height))) return null;
      if (!Array.isArray(p.options) || p.options.length < 1 || p.options.length > 3 || !Array.isArray(p.variants) || p.variants.length < 1 || p.variants.length > 50 || p.variantCount !== p.variants.length) return null;
      for (const [index, option] of p.options.entries()) {
        if (!object(option) || option.position !== index + 1 || !text(option.name) || !Array.isArray(option.values) || !option.values.length || option.values.length > 50 || !unique(option.values.map(v => v.id)) || !option.values.every(v => object(v) && id(v.id) && text(v.name))) return null;
      }
      const combinations = new Set();
      for (const v of p.variants) {
        if (!object(v) || unsupported(v) || !id(v.id) || variantIds.has(v.id) || v.productId !== p.id || typeof v.available !== 'boolean' || v.requiresSellingPlan !== false || v.sellingPlanCount !== 0 || v.quantityPriceBreaks !== false) return null;
        variantIds.add(v.id);
        if (!Array.isArray(v.optionValueIds) || v.optionValueIds.length !== p.options.length || !v.optionValueIds.every((value, i) => p.options[i].values.some(o => o.id === value))) return null;
        const combination = v.optionValueIds.join(':'); if (combinations.has(combination)) return null; combinations.add(combination);
        if (!object(v.price) || !natural(v.price.amount) || !text(v.price.formatted) || v.price.currency !== c.currency || v.price.amountDomain !== c.amountDomain || (['JPY', 'KRW'].includes(c.currency) && v.price.amount % 100 !== 0)) return null;
        const q = v.quantity;
        if (!object(q) || q.minimum !== 1 || q.increment !== 1 || !(q.maximum === null || positive(q.maximum))) return null;
        const stock = v.inventory;
        if (!object(stock) || !['unknown', 'shopify-liquid-variant'].includes(stock.source)) return null;
        if (stock.source === 'shopify-liquid-variant' && (!(stock.management === null || text(stock.management)) || !['continue', 'deny'].includes(stock.policy) || !(stock.quantity === null || Number.isSafeInteger(stock.quantity)))) return null;
      }
      if (p.available !== p.variants.some(v => v.available)) return null;
    }
    if (!input.products.some(p => p.id === c.productId)) return null;
    return freeze(copy(input));
  } catch { return null; }
}

export function createPackSelection(input, formatter) {
  const model = validatePack(input);
  let alive = Boolean(model), rows = [], sequence = 0;
  const newRow = productId => {
    const product = model?.products.find(p => p.id === productId);
    return {key: String(++sequence), productId: product?.id || '', choices: product?.options.map(() => '') || []};
  };
  const reset = () => { if (alive) rows = packTiers(model.source).slice(0, 1).flatMap(n => Array.from({length:n}, (_, index) => newRow(index === 0 ? model.context.productId : undefined))); };
  reset();
  const snapshot = () => {
    if (!alive) return freeze({valid: false, status: 'invalid', rows: [], aggregate: [], subtotal: null, complete: false, cartReady: false});
    const resolved = rows.map(row => {
      const product = model.products.find(p => p.id === row.productId);
      const variant = product && row.choices.every(Boolean) ? product.variants.find(v => v.optionValueIds.every((value, i) => value === row.choices[i])) : null;
      const status = !product || row.choices.some(v => !v) ? 'incomplete' : !variant ? 'missingCombination' : !variant.available ? 'unavailable' : 'ready';
      return {...copy(row), variant: variant || null, status};
    });
    const grouped = new Map();
    for (const row of resolved) if (row.variant) {
      const previous = grouped.get(row.variant.id);
      if (previous) previous.quantity++; else grouped.set(row.variant.id, {productId: row.productId, variantId: row.variant.id, quantity: 1, variant: row.variant});
    }
    for (const group of grouped.values()) {
      const v = group.variant, stock = v.inventory;
      let issue = v.quantity.maximum !== null && group.quantity > v.quantity.maximum ? 'quantityExceeded' : null;
      if (!issue && (stock.source === 'unknown' || (stock.management !== null && stock.policy === 'deny' && stock.quantity === null))) issue = 'quantityUnknown';
      if (!issue && stock.source === 'shopify-liquid-variant' && stock.management !== null && stock.policy === 'deny' && group.quantity > stock.quantity) issue = 'quantityExceeded';
      if (issue) resolved.filter(row => row.variant?.id === v.id && row.status === 'ready').forEach(row => {row.status = issue;});
    }
    const complete = resolved.every(row => row.status === 'ready');
    let subtotal = null;
    if (complete && formatter?.status === 'reviewed' && text(formatter.revision) && typeof formatter.format === 'function') {
      const amount = resolved.reduce((sum, row) => sum + row.variant.price.amount, 0);
      if (natural(amount)) {
        try {
          const formatted = formatter.format(freeze({amount, currency: model.context.currency, amountDomain: model.context.amountDomain}));
          if (text(formatted)) subtotal = {amount, formatted, currency: model.context.currency, amountDomain: model.context.amountDomain, formatterRevision: formatter.revision};
          else if (formatted && typeof formatted.then === 'function') Promise.resolve(formatted).catch(() => {});
        } catch { /* Per-variant native money remains authoritative. */ }
      }
    }
    return freeze({valid: true, status: complete ? 'ready' : resolved.find(row => row.status !== 'ready').status, rows: resolved, aggregate: [...grouped.values()].map(({variant, ...group}) => group), subtotal, complete, cartReady: false});
  };
  return Object.freeze({model, reset, snapshot,
    setTier(quantity) {
      if (!alive || !packTiers(model.source).includes(quantity)) return false;
      rows = Array.from({length: quantity}, (_, index) => rows[index] || newRow()); return true;
    },
    chooseProduct(key, productId) {
      const row = alive && rows.find(r => r.key === key), product = model?.products.find(p => p.id === productId);
      if (!row || (productId !== '' && !product)) return false;
      if (row.productId !== productId) {row.productId = productId; row.choices = product?.options.map(() => '') || [];}
      return true;
    },
    chooseOption(key, position, valueId) {
      const row = alive && rows.find(r => r.key === key), product = model?.products.find(p => p.id === row?.productId), option = product?.options[position - 1];
      if (!row || !option || (valueId !== '' && !option.values.some(v => v.id === valueId))) return false;
      row.choices[position - 1] = valueId; return true;
    },
    exportSelection(expectedContext) {
      const state = snapshot();
      if (!state.complete || !sameContext(expectedContext, model.context)) return null;
      return freeze({version: 1, instanceId: model.instanceId, context: copy(model.context), source: copy(model.source), tier: rows.length,
        rows: state.rows.map(row => ({rowKey: row.key, productId: row.productId, variantId: row.variant.id, optionValueIds: [...row.choices], quantity: 1})),
        aggregate: copy(state.aggregate), subtotal: state.subtotal, cartReady: false, discountEligibility: 'unverified', stockReservation: false});
    },
    dispose() {alive = false; rows = [];}
  });
}

export function registerPackSelector(win) {
  if (!win?.customElements || win.customElements.get('raith-pack-selector')) return;
  win.customElements.define('raith-pack-selector', class extends win.HTMLElement {
    connectedCallback() {
      this.listeners?.abort(); this.observer?.disconnect(); this.listeners = new win.AbortController();
      this.drawer = this.querySelector('vl-size-drawer.raith-pack-dialog'); this.script = this.querySelector('[data-pack-config]');
      this.template = this.querySelector('template[data-pack-item-template]');
      if (!this.drawer || !this.script || !this.template) return;
      const {signal} = this.listeners;
      this.drawer.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !this.drawer.open || event.defaultPrevented) return;
        event.preventDefault(); event.stopPropagation(); this.drawer.hide();
      }, {signal});
      this.drawer.addEventListener('dialog:before-show', event => {
        if (event.target !== this.drawer) return;
        if (this.submission) {this.submission.cancel(); this.drawer.hide(); return;}
        if (!this.current() || !this.core?.snapshot().valid || this.closing) {this.drawer.hide(); return;}
        this.cartConnection?.changed(); this.core.reset(); this.render();
      }, {signal});
      this.drawer.addEventListener('dialog:before-hide', event => {
        if (event.target !== this.drawer) return;
        const drawer = this.drawer;
        if (!this.leave || this.leave.drawer !== drawer) {
          const leave = {drawer};
          const finish = event => {if (event.target === drawer) leave.finish();};
          leave.promise = new Promise(resolve => {leave.finish = () => {drawer.removeEventListener('dialog:after-hide', finish); if (this.leave === leave) this.leave = null; resolve();};});
          drawer.addEventListener('dialog:after-hide', finish);
          this.leave = leave;
        }
        const owned = this.submission?.consumeClose(drawer);
        if (!owned) this.submission?.cancel();
        this.closing = true; if (!owned) this.core?.reset(); this.setEntries(false); this.refreshCartConnection();
      }, {signal});
      this.drawer.addEventListener('dialog:after-hide', event => {if (event.target === this.drawer && !this.drawer.open) {this.closing = false; this.setEntries(Boolean(this.core?.snapshot().valid)); this.refreshCartConnection();}}, {signal});
      this.addEventListener('change', event => {
        if (!this.current()) {this.revoke(); return;}
        const control = event.target.closest('select[data-pack-row]');
        if (!control || control.closest('raith-pack-selector') !== this || !this.core || this.closing || this.submission) return;
        this.cartConnection?.changed();
        if (control.hasAttribute('data-pack-product')) this.core.chooseProduct(control.dataset.packRow, control.value);
        else this.core.chooseOption(control.dataset.packRow, Number(control.dataset.packOption), control.value);
        this.render();
      }, {signal});
      this.addEventListener('click', event => {
        const add = event.target.closest('[data-pack-add]');
        if (add && add.closest('raith-pack-selector') === this) {
          event.preventDefault();
          const connection = this.cartConnection;
          if (!connection || !this.refreshCartConnection() || this.cartActivating) return;
          this.cartActivating = true; this.refreshCartConnection();
          try {Promise.resolve(connection.submit()).catch(() => {}).finally(() => {this.cartActivating = false; this.refreshCartConnection();});}
          catch {this.cartActivating = false; this.refreshCartConnection();}
          return;
        }
        const tier = event.target.closest('[data-pack-tier]');
        if (!tier || tier.closest('raith-pack-selector') !== this) return;
        if (!this.current()) {this.revoke(); return;}
        if (!this.closing && !this.submission && this.core?.setTier(Number(tier.dataset.packTier))) {this.cartConnection?.changed(); this.render();}
      }, {signal});
      this.observer = new win.MutationObserver(() => {if (!this.structureCurrent()) this.revoke(true); else if (this.script.textContent !== this.scriptVersion) this.read();});
      this.observer.observe(this, {subtree: true, childList: true, characterData: true}); this.read();
    }
    structureCurrent() {
      const rowValid = node => node?.matches('[data-pack-item]') && ['[data-pack-number]', '[data-pack-title]', '[data-pack-controls]', '[data-pack-price]', '[data-pack-state]', 'img'].every(selector => node.querySelector(selector));
      const list = this.drawer?.querySelector('[data-pack-items]');
      return this.isConnected && this.querySelector('[data-pack-config]') === this.script && this.querySelector('vl-size-drawer.raith-pack-dialog') === this.drawer && this.querySelector('template[data-pack-item-template]') === this.template
        && ['[data-pack-items]', '[data-pack-tiers]', '[data-pack-subtotal]', '[data-pack-status]', '[data-pack-add]', '[data-pack-label="heading"]'].every(selector => this.drawer?.querySelector(selector))
        && this.template?.content.childElementCount === 1 && rowValid(this.template.content.firstElementChild) && [...list.children].every(rowValid);
    }
    current() {return this.structureCurrent() && this.script.textContent === this.scriptVersion;}
    entries() {return [...this.ownerDocument.querySelectorAll('[data-pack-target]')].filter(e => e.dataset.packTarget === this.dataset.packInstance);}
    setEntries(ready) {
      if (this.submission?.isCurrent()) {this.entries().forEach(entry => {entry.disabled = false; entry.hidden = false;}); return;}
      this.entries().forEach(entry => {entry.disabled = !ready || this.closing; entry.hidden = !ready && !this.closing;});
      this.ownerDocument.querySelectorAll('[data-pack-entry-wrapper]').forEach(wrapper => {if (wrapper.dataset.packEntryWrapper === this.dataset.packInstance) wrapper.hidden = !ready && !this.closing;});
    }
    revoke(detach = false) {
      this.cartConnection?.disconnect();
      this.submission?.cancel();
      if (detach) {this.listeners?.abort(); this.observer?.disconnect();}
      this.dataset.packState = 'invalid';
      const error = this.querySelector('[data-pack-error]'); if (error) error.hidden = !error.textContent.trim();
      this.generation = (this.generation || 0) + 1; this.cancel?.(); this.core?.dispose(); this.core = null;
      this.setEntries(false); if (this.drawer?.open) this.drawer.hide();
      if (this.drawer) {this.drawer.hidden = true; this.drawer.querySelectorAll('select,button').forEach(e => {e.disabled = true;});}
    }
    read() {let config; try {config = JSON.parse(this.script.textContent);} catch {config = null;} this.configure(config);}
    async configure(config, formatter) {
      this.revoke(); const generation = this.generation; this.scriptVersion = this.script?.textContent;
      const core = createPackSelection(config, formatter);
      if (this.listeners?.signal.aborted || !core.model || core.model.instanceId !== this.dataset.packInstance || this.drawer?.id !== `raith-pack-${core.model.instanceId}`) return false;
      const cancelled = new Promise(resolve => {this.cancel = () => resolve(false);});
      const wait = promise => Promise.race([Promise.resolve(promise).then(() => true), cancelled]);
      if (!await wait(win.customElements.whenDefined('vl-size-drawer'))) {core.dispose(); return false;}
      while (this.leave?.drawer === this.drawer) if (!await wait(this.leave.promise)) {core.dispose(); return false;}
      if (generation !== this.generation || !this.current()) {core.dispose(); return false;}
      const duplicates = [...this.ownerDocument.querySelectorAll('raith-pack-selector')].filter(e => e !== this && e.dataset.packInstance === this.dataset.packInstance);
      if (duplicates.length) {duplicates.forEach(e => e.revoke?.()); core.dispose(); return false;}
      const error = this.querySelector('[data-pack-error]'); if (error) error.hidden = true;
      this.cancel = null; this.core = core; this.closing = false; this.drawer.hidden = false; this.render(); this.setEntries(true); return true;
    }
    beginSubmission(expectedContext) {
      if (this.submission || !this.current() || !this.drawer?.open || this.closing) return null;
      const selected = this.exportSelection(expectedContext);
      if (!selected) return null;
      const core = this.core, generation = this.generation, drawer = this.drawer;
      const context = selected.context, signature = JSON.stringify(selected), listeners = new win.AbortController();
      const ownShow = Object.getOwnPropertyDescriptor(drawer, 'show');
      let active = true, closing = false, hidden = false, finished = false, marker = null, promise, resolve;
      const owns = () => this.submission === record && this.core === core && this.generation === generation && this.drawer === drawer;
      const valid = () => active && owns() && this.current() && JSON.stringify(core.exportSelection(context)) === signature;
      const settle = value => {if (resolve) {resolve(value); resolve = null;}};
      const complete = () => {if (hidden && finished) settle(valid() && !drawer.open);};
      const stop = cancel => {
        if (!active) return;
        const owned = owns(), usable = owned && this.current();
        active = false; marker = null; listeners.abort(); settle(false);
        if (drawer.show === blockShow) {if (ownShow) Object.defineProperty(drawer, 'show', ownShow); else delete drawer.show;}
        if (this.submission === record) this.submission = null;
        if (owned) {
          if (usable) {if (cancel || closing) core.reset(); this.render(); this.setEntries(true);}
        }
      };
      const blockShow = () => Promise.resolve(false);
      const record = {
        isCurrent: valid,
        cancel: () => stop(true),
        consumeClose: target => {
          if (marker !== target || !valid()) return false;
          marker = null; closing = true; return true;
        }
      };
      this.submission = record;
      drawer.show = blockShow;
      const {signal} = listeners;
      // Keep opener focusable; capture activation rather than disabling the focus-return target.
      const dismiss = event => {
        const path = event.composedPath(), control = event.target.closest?.('[aria-controls]');
        if (control?.getAttribute('aria-controls') === drawer.id) {if (event.type === 'click') {event.preventDefault(); event.stopImmediatePropagation();} return;}
        if (!(drawer.open || (closing && !hidden))) return;
        const overlay = path.some(node => node.getAttribute?.('part')?.split(/\s+/).includes('overlay'));
        if (overlay || !path.includes(drawer)) record.cancel();
      };
      this.ownerDocument.addEventListener('click', dismiss, {capture: true, signal});
      this.ownerDocument.addEventListener('touchend', dismiss, {capture: true, signal});
      this.ownerDocument.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !(drawer.open || (closing && !hidden))) return;
        record.cancel();
      }, {capture: true, signal});
      drawer.addEventListener('dialog:force-close', () => record.cancel(), {capture: true, signal});
      drawer.addEventListener('dialog:after-hide', event => {
        if (event.target !== drawer || !closing) return;
        hidden = true; complete();
      }, {signal});
      this.render(); this.setEntries(true);
      return Object.freeze({
        isCurrent: () => {if (!valid()) {record.cancel(); return false;} return true;},
        closeForPresentation: () => {
          if (promise) return promise;
          promise = new Promise(done => {resolve = done;});
          if (!valid() || !drawer.open || this.closing) {record.cancel(); settle(false); return promise;}
          marker = drawer;
          const factory = drawer.createLeaveAnimationControls, ownFactory = Object.getOwnPropertyDescriptor(drawer, 'createLeaveAnimationControls');
          let barrier;
          const failed = () => {
            if (barrier && this.leave === barrier && this.core === core && this.generation === generation && this.drawer === drawer && (!this.submission || this.submission === record)) this.closing = false;
            barrier?.finish(); record.cancel();
          };
          const observeLeave = (...args) => {
            if (closing && this.leave?.drawer === drawer) barrier = this.leave;
            try {
              const controls = factory.apply(drawer, args);
              // Native hide waits for an event, not this promise; observe real animation rejection too.
              Promise.resolve(controls.finished).then(() => {}, failed);
              return controls;
            } catch (error) {failed(); throw error;}
          };
          drawer.createLeaveAnimationControls = observeLeave;
          try {
            const leave = drawer.hide();
            if (marker) record.cancel(); // Native before-hide must consume authorization synchronously.
            Promise.resolve(leave).then(() => {finished = true; complete();}, failed);
          } catch {failed();}
          finally {
            marker = null;
            if (drawer.createLeaveAnimationControls === observeLeave) {if (ownFactory) Object.defineProperty(drawer, 'createLeaveAnimationControls', ownFactory); else delete drawer.createLeaveAnimationControls;}
          }
          return promise;
        },
        release: () => stop(false)
      });
    }
    connectCart({expectedContext, labels, getState, submit, changed} = {}) {
      if (this.cartConnection || !this.current() || !this.core?.model || !sameContext(expectedContext,this.core.model.context) || !object(labels) || !packCartConnectionLabels.every(key=>text(labels[key])) || ![getState,submit,changed].every(fn=>typeof fn==='function')) return null;
      const core=this.core, generation=this.generation, context=freeze(copy(expectedContext)), messages=freeze(copy(labels));
      const owns=()=>this.cartConnection===connection && this.core===core && this.generation===generation && this.current();
      const connection={context,labels:messages,getState,submit,changed,
        current:owns,
        ready:()=>owns() && Boolean(this.drawer?.open) && !this.drawer.hidden && !this.closing && !this.submission && Boolean(core.exportSelection(context)) && core.snapshot().rows.every(row=>Boolean(core.model.products.find(p=>p.id===row.productId)?.image)),
        disconnect:()=>{if(this.cartConnection===connection){this.cartConnection=null;this.refreshCartConnection();}}
      };
      this.cartConnection=connection; this.refreshCartConnection();
      return Object.freeze({isCurrent:owns,isReady:connection.ready,refresh:()=>this.refreshCartConnection(),disconnect:connection.disconnect,
        retire:selection=>{if(this.core!==core || this.generation!==generation || !this.current() || this.submission || JSON.stringify(core.exportSelection(context))!==JSON.stringify(selection))return false;core.reset();this.render();return true;}});
    }
    refreshCartConnection() {
      const button=this.querySelector('[data-pack-add]'), status=this.querySelector('[data-pack-status]');
      if(!button || !status)return false;
      const connection=this.cartConnection, state=this.core?.snapshot();
      let ready=false,busy=false,checking=false,message=state?.valid?`${this.core.model.labels[state.status]} ${this.core.model.labels.selectionOnly}`:'',add=this.core?.model?.labels.add;
      if(connection) {
        add=connection.labels.add;
        let remote;
        try {remote=connection.getState();if(remote && typeof remote.then==='function'){Promise.resolve(remote).catch(()=>{});remote=null;}}catch{remote=null;}
        busy=remote?.busy===true || Boolean(this.cartActivating);
        checking=remote?.checking===true;
        const images=state?.rows.every(row=>Boolean(this.core?.model.products.find(p=>p.id===row.productId)?.image));
        ready=remote?.available===true && remote?.imagesVerified===true && !busy && connection.ready();
        const key=remote?.message && remote.message!=='idle' ? remote.message : checking ? 'checking' : !connection.current() || remote?.available!==true ? 'unavailable' : !state?.complete ? 'incomplete' : !images || remote?.imagesVerified!==true ? 'missingImages' : 'ready';
        message=connection.labels[key] || connection.labels.unavailable;
      }
      if(button.disabled===ready)button.disabled=!ready;
      if(button.getAttribute('aria-busy')!==String(busy))button.setAttribute('aria-busy',String(busy));
      if(add && button.textContent!==add)button.textContent=add;
      if(status.textContent!==message)status.textContent=message;
      if(status.getAttribute('aria-live')!=='polite')status.setAttribute('aria-live','polite');
      if(status.getAttribute('aria-busy')!==String(busy || checking))status.setAttribute('aria-busy',String(busy || checking));
      return ready;
    }
    exportSelection(context) {if (!this.current()) {this.revoke(); return null;} return this.core?.exportSelection(context) || null;}
    render() {
      if (!this.current()) {this.revoke(); return;}
      const state = this.core?.snapshot(); if (!state?.valid) return;
      this.dataset.packState = state.status;
      const {labels, products, instanceId} = this.core.model, d = this.ownerDocument;
      const tierContainer = this.querySelector('[data-pack-tiers]'), tiers = packTiers(this.core.model.source);
      const buttons = [...tierContainer.querySelectorAll('[data-pack-tier]')];
      if (buttons.length !== tiers.length || buttons.some((button, index) => Number(button.dataset.packTier) !== tiers[index])) {
        tierContainer.replaceChildren(...tiers.map(quantity => {
          const button = d.createElement('button'); button.type = 'button';
          button.dataset.packTier = String(quantity);
          const label = d.createElement('span'); label.dataset.packLabel = `tier${quantity}`; button.append(label);
          return button;
        }));
      }
      this.querySelectorAll('[data-pack-label]').forEach(e => {e.textContent = labels[e.dataset.packLabel];});
      this.querySelectorAll('[data-pack-tier]').forEach(button => {
        button.disabled = Boolean(this.submission);
        button.setAttribute('aria-pressed', String(Number(button.dataset.packTier) === state.rows.length));
        const value = labels[`tier${button.dataset.packTier}Subtext`];
        const copy = typeof value === 'string' ? value.trim() : '';
        let subtext = button.querySelector('.raith-pack-tier__subtext');
        if (copy) {
          if (!subtext) {subtext = d.createElement('span'); subtext.className = 'raith-pack-tier__subtext'; button.append(subtext);}
          subtext.textContent = copy;
        } else subtext?.remove();
      });
      this.querySelector('[data-pack-tiers]').setAttribute('aria-label', labels.quantity);
      const list = this.querySelector('[data-pack-items]');
      const rowKeys = new Set(state.rows.map(r => r.key));
      [...list.children].forEach(e => {if (!rowKeys.has(e.dataset.packItem)) e.remove();});
      for (const [index, row] of state.rows.entries()) {
        let node = [...list.children].find(e => e.dataset.packItem === row.key);
        if (!node) {node = this.template.content.firstElementChild.cloneNode(true); node.dataset.packItem = row.key; list.append(node);}
        const product = products.find(p => p.id === row.productId);
        node.querySelector('[data-pack-number]').textContent = `${labels.item} ${String(index + 1).padStart(2, '0')}`;
        node.querySelector('[data-pack-title]').textContent = product?.title || labels.chooseProduct;
        const img = node.querySelector('img'); img.hidden = !product?.image;
        if (product?.image) {img.src = product.image.url; img.alt = product.image.alt; img.width = product.image.width; img.height = product.image.height;} else img.removeAttribute('src');
        const missingImage = node.querySelector('[data-pack-image-unavailable]'); if (missingImage) missingImage.hidden = !product || Boolean(product.image);
        node.querySelector('[data-pack-price]').textContent = row.variant?.price.formatted || '';
        node.querySelector('[data-pack-state]').textContent = labels[row.status];
        const controls = node.querySelector('[data-pack-controls]'), retained = new Set();
        const existing = new Map([...controls.querySelectorAll('select')].map(select => [select.id, select]));
        const addSelect = (suffix, labelText, placeholder, values, selected, position) => {
          const identity = `${instanceId}-r${row.key}-${suffix}`; retained.add(identity);
          let select = existing.get(identity);
          if (!select) {
            const label = d.createElement('label'), caption = d.createElement('span'); select = d.createElement('select');
            select.id = identity; select.dataset.packRow = row.key;
            if (position) select.dataset.packOption = String(position); else select.dataset.packProduct = '';
            label.htmlFor = identity; caption.className = 'raith-pack-visually-hidden';
            label.append(caption, select); controls.append(label);
          }
          const caption = select.previousElementSibling, description = `${labels.item} ${index + 1}: ${labelText}`;
          if (caption.textContent !== description) caption.textContent = description;
          const options = [{id: '', name: placeholder}, ...values];
          // Keep the active native picker attached; only a changed product schema replaces its options.
          if (select.options.length !== options.length || options.some((value, i) => select.options[i].value !== value.id || select.options[i].textContent !== value.name)) {
            select.replaceChildren(...options.map(value => new win.Option(value.name, value.id)));
          }
          if (select.value !== selected) select.value = selected;
          if (select.disabled !== Boolean(this.submission)) select.disabled = Boolean(this.submission);
        };
        addSelect('product', labels.colour, labels.chooseProduct, products.map(p => ({id: p.id, name: p.colour || p.title})), row.productId);
        product?.options.forEach((option, i) => addSelect(`o${option.position}`, option.name, `${labels.chooseOption} ${option.name}`, option.values, row.choices[i], option.position));
        existing.forEach((select, identity) => {if (!retained.has(identity)) select.parentElement.remove();});
      }
      const total = this.querySelector('[data-pack-subtotal]'); total.textContent = state.subtotal?.formatted || ''; total.parentElement.hidden = !state.subtotal;
      this.refreshCartConnection();
    }
    disconnectedCallback() {this.revoke(); this.listeners?.abort(); this.observer?.disconnect();}
  });
}
if (typeof window !== 'undefined') registerPackSelector(window);
